import { useEffect, useMemo, useRef, useState } from 'react'

import { Popover, TextInput } from '@mantine/core'
import { MonthPicker } from '@mantine/dates'

import IconCalendar from 'src/icons/IconCalendar'

// Helper function to format a Date as MM/YYYY
const formatDate = (date: Date): string => {
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const year = date.getFullYear().toString()
  return `${month}/${year}`
}

interface MonthInputProps {
  editing: boolean
  value: string | Date | null | undefined
  handleChange?: (date: Date | null) => void
  _minDate?: string
  _maxDate?: string
  label?: string
  required?: boolean
  disabled?: boolean
  error?: string
  [key: string]: any
}

const MonthInput = ({
  editing,
  value,
  handleChange,
  _minDate = '1930-01-01',
  _maxDate = '2100-01-01',
  label,
  required,
  disabled,
  error,
  ...props
}: MonthInputProps) => {
  const [inputValue, setInputValue] = useState('')
  const [pickerOpened, setPickerOpened] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const isTypingRef = useRef(false)

  // Convert value to Date for internal use, creating Date for MonthPicker
  // Memoize to prevent creating new Date objects on every render
  const dateValue = useMemo(() => {
    if (!value) return null

    let parsed: Date
    if (typeof value === 'string') {
      // Parse ISO string from backend
      parsed = new Date(value)
    } else {
      // Use Date object as is
      parsed = value
    }

    // Check if valid date
    if (isNaN(parsed.getTime())) return null

    // Extract year/month - use UTC for ISO strings, local for Date objects
    // This ensures backend dates (UTC ISO strings) are interpreted correctly
    let year: number, month: number
    if (typeof value === 'string') {
      year = parsed.getUTCFullYear()
      month = parsed.getUTCMonth()
    } else {
      year = parsed.getFullYear()
      month = parsed.getMonth()
    }

    // Create local Date with the year/month from parsed value
    return new Date(year, month, 1, 12, 0, 0, 0)
  }, [value])

  // Update input value when value prop changes (but not while user is typing)
  useEffect(() => {
    if (!isTypingRef.current) {
      if (dateValue) {
        // Format as local date since dateValue is a local Date object
        const formatted = formatDate(dateValue)
        setInputValue(formatted)
      } else {
        setInputValue('')
      }
    }
  }, [dateValue])

  // Format input as MM/YYYY while typing
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    isTypingRef.current = true
    const input = e.target
    const oldValue = input.value
    const cursorPosition = input.selectionStart || 0

    // Count how many digits are before the cursor
    const digitsBeforeCursor = oldValue
      .slice(0, cursorPosition)
      .replace(/\D/g, '').length

    let inputValue = oldValue.replace(/\D/g, '') // Remove non-numeric characters

    if (inputValue.length > 6) inputValue = inputValue.slice(0, 6) // Restrict to 6 characters (MMYYYY)

    const formattedValue = inputValue.replace(
      /^(\d{2})(\d{0,4})$/,
      (_, m, y) => {
        let result = m
        if (y) result += '/' + y
        return result
      }
    )

    // Get the last valid formatted value from dateValue to revert to if validation fails
    const lastValidFormattedValue = dateValue
      ? formatDate(dateValue)
      : ''

    let finalFormattedValue = formattedValue
    let shouldUpdateForm = false
    let parsedDate: Date | null = null

    // If we have a complete date (MM/YYYY), validate it before updating the display
    if (formattedValue.length === 7) {
      const cleaned = formattedValue.replace(/\D/g, '')
      const month = cleaned.slice(0, 2)
      const year = cleaned.slice(2, 6)
      const monthNum = parseInt(month, 10)

      // Validate month (01-12)
      if (monthNum >= 1 && monthNum <= 12) {
        // Create date at noon to match dateValue and handlePickerChange normalization
        parsedDate = new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1, 12, 0, 0, 0)

        // Validate min/max dates
        // Normalize min/max dates to noon for consistent comparison
        const minDateObj = _minDate
          ? (() => {
            const date = new Date(_minDate)
            const year = date.getUTCFullYear()
            const month = date.getUTCMonth()
            return new Date(year, month, 1, 12, 0, 0, 0)
          })()
          : undefined
        const maxDateObj = _maxDate
          ? (() => {
            const date = new Date(_maxDate)
            const year = date.getUTCFullYear()
            const month = date.getUTCMonth()
            return new Date(year, month, 1, 12, 0, 0, 0)
          })()
          : undefined

        // Check if date is within valid range
        const isWithinRange =
          (!minDateObj || parsedDate >= minDateObj) &&
          (!maxDateObj || parsedDate <= maxDateObj)

        if (isWithinRange) {
          // Valid date - keep the formatted value and update form
          shouldUpdateForm = true
        } else {
          // Invalid date - revert to last valid value
          finalFormattedValue = lastValidFormattedValue
        }
      } else {
        // Invalid month - revert to last valid value
        finalFormattedValue = lastValidFormattedValue
      }
    } else if (formattedValue.length === 0) {
      // Empty input - clear the value
      shouldUpdateForm = true
      parsedDate = null
    }
    // For incomplete dates (length < 7), allow typing without validation

    // Update the display with the final value (either the new formatted value or reverted value)
    setInputValue(finalFormattedValue)

    // Update form value if validation passed
    if (shouldUpdateForm && handleChange) {
      handleChange(parsedDate)
    }

    // Calculate new cursor position based on digit count
    // Use the final value that was actually set in the input
    let newCursorPosition = finalFormattedValue.length
    if (digitsBeforeCursor > 0 && finalFormattedValue === formattedValue) {
      let digitCount = 0
      for (let i = 0; i < finalFormattedValue.length; i++) {
        if (/\d/.test(finalFormattedValue[i])) {
          digitCount++
          if (digitCount === digitsBeforeCursor) {
            // Place cursor after this digit
            newCursorPosition = i + 1
            break
          }
        }
      }
    } else if (finalFormattedValue !== formattedValue) {
      // If we reverted, place cursor at the end
      newCursorPosition = finalFormattedValue.length
    }

    // Use setTimeout to set cursor position after React updates the DOM
    setTimeout(() => {
      const inputElement = inputRef.current?.querySelector('input') || input
      if (inputElement && inputElement.setSelectionRange) {
        inputElement.setSelectionRange(newCursorPosition, newCursorPosition)
      }
      isTypingRef.current = false
    }, 0)
  }

  // Handle MonthPicker changes
  const handlePickerChange = (date: Date) => {
    if (handleChange) {
      // The Date object already represents a moment in time, we need to convert it to local time
      const utcOffset = date.getTimezoneOffset() // offset in minutes
      const dateWithOffset = new Date(date.getTime() + utcOffset * 60 * 1000)

      const localYear = dateWithOffset.getFullYear()
      const localMonth = dateWithOffset.getMonth()
      // Create a local date with the year/month from the converted local time
      const normalizedDate = new Date(localYear, localMonth, 1, 12, 0, 0, 0)

      handleChange(normalizedDate)
      setPickerOpened(false)
    }
  }

  if (!editing) {
    return (
      <TextInput
        label={label}
        value={inputValue || '-'}
        variant="unstyled"
        disabled
        classNames={{
          label: 'text-sm font-medium text-doubleNickel-gray-700',
          input: 'text-md text-doubleNickel-gray-900',
        }}
        {...props}
      />
    )
  }

  return (
    <Popover
      opened={pickerOpened}
      onChange={setPickerOpened}
      position="bottom-start"
      withArrow
    >
      <Popover.Target>
        <div ref={inputRef}>
          <TextInput
            label={label}
            required={required}
            value={inputValue}
            onChange={handleInputChange}
            placeholder="MM/YYYY"
            disabled={disabled}
            maxLength={7}
            error={error}
            rightSection={
              <div
                onClick={(e) => {
                  e.stopPropagation()
                  setPickerOpened(!pickerOpened)
                }}
                className="cursor-pointer"
              >
                <IconCalendar className="fill-none stroke-doubleNickel-gray-500" />
              </div>
            }
            classNames={{
              label: 'text-sm font-medium text-doubleNickel-gray-700',
              input: 'text-md text-doubleNickel-gray-900',
            }}
            {...props}
          />
        </div>
      </Popover.Target>
      <Popover.Dropdown p="md">
        <MonthPicker
          minDate={
            _minDate
              ? (() => {
                // Parse ISO string and extract UTC components
                const date = new Date(_minDate)
                const year = date.getUTCFullYear()
                const month = date.getUTCMonth()
                return new Date(year, month, 1, 12, 0, 0, 0)
              })()
              : new Date(1900, 0, 1, 12, 0, 0, 0)
          }
          maxDate={
            _maxDate
              ? (() => {
                // Parse ISO string and extract UTC components
                const date = new Date(_maxDate)
                const year = date.getUTCFullYear()
                const month = date.getUTCMonth()
                return new Date(year, month, 1, 12, 0, 0, 0)
              })()
              : new Date(2100, 0, 1, 12, 0, 0, 0)
          }
          value={dateValue}
          onChange={handlePickerChange}
        />
      </Popover.Dropdown>
    </Popover>
  )
}

export default MonthInput
